import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { API_URL } from "../config/api";
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  History,
  Plus,
  Edit,
  Trash2,
  Calendar,
  User,
  CheckCircle,
  Bug,
  Sparkles,
  Image,
  FileText,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  Rocket
} from "lucide-react";

const DeploymentLogPage = () => {
  const { getAuthHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAutoLogModal, setShowAutoLogModal] = useState(false);
  const [autoLogData, setAutoLogData] = useState({
    title: "",
    summary: "",
    features: "",
    fixes: "",
    changes: ""
  });
  
  const [formData, setFormData] = useState({
    version: "",
    title: "",
    summary: "",
    changes: "",
    fixes: "",
    features: "",
    screenshots: "",
    notes: ""
  });

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/deployment-log`, {
        headers: getAuthHeaders()
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error("Error fetching deployment logs:", error);
      toast.error("Failed to fetch deployment logs");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const resetForm = () => {
    setFormData({
      version: "",
      title: "",
      summary: "",
      changes: "",
      fixes: "",
      features: "",
      screenshots: "",
      notes: ""
    });
  };

  const handleCreate = () => {
    resetForm();
    setEditingLog(null);
    setShowCreateModal(true);
  };

  const handleEdit = (log) => {
    setFormData({
      version: log.version || "",
      title: log.title || "",
      summary: log.summary || "",
      changes: (log.changes || []).join("\n"),
      fixes: (log.fixes || []).join("\n"),
      features: (log.features || []).join("\n"),
      screenshots: (log.screenshots || []).join("\n"),
      notes: log.notes || ""
    });
    setEditingLog(log);
    setShowCreateModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.summary) {
      toast.error("Title and summary are required");
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        version: formData.version,
        title: formData.title,
        summary: formData.summary,
        changes: formData.changes.split("\n").filter(s => s.trim()),
        fixes: formData.fixes.split("\n").filter(s => s.trim()),
        features: formData.features.split("\n").filter(s => s.trim()),
        screenshots: formData.screenshots.split("\n").filter(s => s.trim()),
        notes: formData.notes
      };

      if (editingLog) {
        await axios.put(
          `${API_URL}/admin/deployment-log/${editingLog.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Deployment log updated");
      } else {
        await axios.post(
          `${API_URL}/admin/deployment-log`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Deployment log created");
      }

      setShowCreateModal(false);
      resetForm();
      fetchLogs();
    } catch (error) {
      console.error("Error saving deployment log:", error);
      toast.error(error.response?.data?.detail || "Failed to save deployment log");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this deployment log?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/deployment-log/${logId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Deployment log deleted");
      fetchLogs();
    } catch (error) {
      console.error("Error deleting deployment log:", error);
      toast.error("Failed to delete deployment log");
    }
  };

  const handleQuickLog = async () => {
    setSaving(true);
    try {
      const payload = {
        title: autoLogData.title || "Application Update",
        summary: autoLogData.summary || "Routine deployment update",
        features: autoLogData.features ? autoLogData.features.split("\n").filter(Boolean) : [],
        fixes: autoLogData.fixes ? autoLogData.fixes.split("\n").filter(Boolean) : [],
        changes: autoLogData.changes ? autoLogData.changes.split("\n").filter(Boolean) : []
      };
      
      await axios.post(
        `${API_URL}/admin/deployment-log/auto`,
        payload,
        { headers: getAuthHeaders() }
      );
      toast.success("Deployment log created automatically");
      setShowAutoLogModal(false);
      setAutoLogData({ title: "", summary: "", features: "", fixes: "", changes: "" });
      fetchLogs();
    } catch (error) {
      console.error("Error creating auto deployment log:", error);
      toast.error(error.response?.data?.detail || "Failed to create deployment log");
    } finally {
      setSaving(false);
    }
  };

  const handleLogFromSession = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/deployment-log/from-session`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Deployment logged: ${response.data.log?.version || 'New version'}`);
      fetchLogs();
    } catch (error) {
      console.error("Error creating session deployment log:", error);
      toast.error(error.response?.data?.detail || "Failed to create deployment log");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto" data-testid="deployment-log-page">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <History className="h-6 w-6 text-indigo-600" />
                Deployment Log
              </h1>
              <p className="text-muted-foreground mt-1">
                Track all changes and updates made to the application
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchLogs} size="sm">
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setShowAutoLogModal(true)} 
                data-testid="quick-log-btn"
              >
                <Sparkles className="h-4 w-4 mr-1" /> Quick Log
              </Button>
              <Button onClick={handleCreate} data-testid="create-deployment-log-btn">
                <Plus className="h-4 w-4 mr-1" /> New Entry
              </Button>
            </div>
          </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Rocket className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{logs.length}</p>
                <p className="text-sm text-muted-foreground">Total Deployments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {logs.reduce((acc, l) => acc + (l.features?.length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Features Added</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Bug className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {logs.reduce((acc, l) => acc + (l.fixes?.length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Bugs Fixed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Deployment History</CardTitle>
          <CardDescription>Click on an entry to expand details</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No deployment logs yet</p>
              <p className="text-sm">Create your first entry to start tracking changes</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log, index) => (
                <div 
                  key={log.id} 
                  className="relative"
                  data-testid={`deployment-log-${log.id}`}
                >
                  {/* Timeline connector */}
                  {index < logs.length - 1 && (
                    <div className="absolute left-6 top-14 w-0.5 h-full bg-slate-200" />
                  )}
                  
                  <div 
                    className={`border rounded-lg transition-all ${
                      expandedLog === log.id ? 'border-indigo-300 shadow-md' : 'hover:border-slate-300'
                    }`}
                  >
                    {/* Header - Always visible */}
                    <div 
                      className="p-4 cursor-pointer flex items-start gap-4"
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                    >
                      {/* Version badge */}
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          v{log.version?.split('.').slice(-1)[0] || '?'}
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            v{log.version}
                          </Badge>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(log.deployed_at)}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {log.deployed_by}
                          </span>
                        </div>
                        <h3 className="font-semibold text-foreground">{log.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {log.summary}
                        </p>
                        
                        {/* Quick stats */}
                        <div className="flex items-center gap-3 mt-2">
                          {log.features?.length > 0 && (
                            <span className="text-xs flex items-center gap-1 text-green-600">
                              <Sparkles className="h-3 w-3" />
                              {log.features.length} features
                            </span>
                          )}
                          {log.fixes?.length > 0 && (
                            <span className="text-xs flex items-center gap-1 text-orange-600">
                              <Bug className="h-3 w-3" />
                              {log.fixes.length} fixes
                            </span>
                          )}
                          {log.changes?.length > 0 && (
                            <span className="text-xs flex items-center gap-1 text-blue-600">
                              <FileText className="h-3 w-3" />
                              {log.changes.length} changes
                            </span>
                          )}
                          {log.screenshots?.length > 0 && (
                            <span className="text-xs flex items-center gap-1 text-purple-600">
                              <Image className="h-3 w-3" />
                              {log.screenshots.length} screenshots
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Expand icon */}
                      <div className="flex-shrink-0">
                        {expandedLog === log.id ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded content */}
                    {expandedLog === log.id && (
                      <div className="px-4 pb-4 pt-0 border-t">
                        <div className="mt-4 space-y-4">
                          {/* Summary */}
                          <div>
                            <h4 className="text-sm font-medium mb-2">Summary</h4>
                            <div className="text-sm text-foreground whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                              {log.summary}
                            </div>
                          </div>
                          
                          {/* Features */}
                          {log.features?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-green-600" />
                                New Features
                              </h4>
                              <ul className="space-y-1">
                                {log.features.map((feature, i) => (
                                  <li key={i} className="text-sm flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Fixes */}
                          {log.fixes?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <Bug className="h-4 w-4 text-orange-600" />
                                Bug Fixes
                              </h4>
                              <ul className="space-y-1">
                                {log.fixes.map((fix, i) => (
                                  <li key={i} className="text-sm flex items-start gap-2">
                                    <CheckCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                    <span>{fix}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Changes */}
                          {log.changes?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <FileText className="h-4 w-4 text-blue-600" />
                                Other Changes
                              </h4>
                              <ul className="space-y-1">
                                {log.changes.map((change, i) => (
                                  <li key={i} className="text-sm flex items-start gap-2">
                                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                                    <span>{change}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Screenshots */}
                          {log.screenshots?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <Image className="h-4 w-4 text-purple-600" />
                                Screenshots
                              </h4>
                              <div className="grid grid-cols-2 gap-2">
                                {log.screenshots.map((url, i) => (
                                  <a 
                                    key={i} 
                                    href={url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img 
                                      src={url} 
                                      alt={`Screenshot ${i + 1}`}
                                      className="rounded-lg border hover:shadow-md transition-shadow max-h-48 object-cover w-full"
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Notes */}
                          {log.notes && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">Additional Notes</h4>
                              <div className="text-sm text-muted-foreground bg-amber-50 p-3 rounded-lg border border-amber-200">
                                {log.notes}
                              </div>
                            </div>
                          )}
                          
                          {/* Actions */}
                          <div className="flex justify-end gap-2 pt-2 border-t">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleEdit(log); }}
                            >
                              <Edit className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={(e) => { e.stopPropagation(); handleDelete(log.id); }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-indigo-600" />
              {editingLog ? "Edit Deployment Log" : "New Deployment Log"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  placeholder="e.g., 1.2.3"
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                />
                <p className="text-xs text-muted-foreground mt-1">Leave blank for auto-increment</p>
              </div>
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., T1 Transit Processing System"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="summary">Summary *</Label>
              <Textarea
                id="summary"
                placeholder="Brief description of this deployment..."
                value={formData.summary}
                onChange={(e) => setFormData({...formData, summary: e.target.value})}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="features" className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-green-600" />
                New Features (one per line)
              </Label>
              <Textarea
                id="features"
                placeholder="T1 Transit email auto-processing&#10;Customer Advisory expanded with email archive&#10;..."
                value={formData.features}
                onChange={(e) => setFormData({...formData, features: e.target.value})}
                rows={4}
              />
            </div>
            
            <div>
              <Label htmlFor="fixes" className="flex items-center gap-2">
                <Bug className="h-4 w-4 text-orange-600" />
                Bug Fixes (one per line)
              </Label>
              <Textarea
                id="fixes"
                placeholder="Fixed duplicate Trinet pending emails&#10;Fixed MRN parsing for Slovenian format&#10;..."
                value={formData.fixes}
                onChange={(e) => setFormData({...formData, fixes: e.target.value})}
                rows={4}
              />
            </div>
            
            <div>
              <Label htmlFor="changes" className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                Other Changes (one per line)
              </Label>
              <Textarea
                id="changes"
                placeholder="Updated staff directory visibility&#10;Improved news HTML rendering&#10;..."
                value={formData.changes}
                onChange={(e) => setFormData({...formData, changes: e.target.value})}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="screenshots" className="flex items-center gap-2">
                <Image className="h-4 w-4 text-purple-600" />
                Screenshot URLs (one per line)
              </Label>
              <Textarea
                id="screenshots"
                placeholder="https://example.com/screenshot1.png&#10;https://example.com/screenshot2.png"
                value={formData.screenshots}
                onChange={(e) => setFormData({...formData, screenshots: e.target.value})}
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any additional notes, warnings, or migration steps..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {editingLog ? "Update" : "Create"} Log
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Log Modal */}
      <Dialog open={showAutoLogModal} onOpenChange={setShowAutoLogModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-green-600" />
              Quick Deployment Log
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Quickly log a deployment with minimal details. Version is auto-incremented.
            </p>
            
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={autoLogData.title}
                onChange={(e) => setAutoLogData({ ...autoLogData, title: e.target.value })}
                placeholder="e.g., O365 Stats Feature"
              />
            </div>

            <div className="space-y-2">
              <Label>Summary *</Label>
              <Textarea
                value={autoLogData.summary}
                onChange={(e) => setAutoLogData({ ...autoLogData, summary: e.target.value })}
                placeholder="Brief description of this deployment"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Features (one per line)</Label>
              <Textarea
                value={autoLogData.features}
                onChange={(e) => setAutoLogData({ ...autoLogData, features: e.target.value })}
                placeholder="O365 Email Activity Stats&#10;CSV Import functionality"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Bug Fixes (one per line)</Label>
              <Textarea
                value={autoLogData.fixes}
                onChange={(e) => setAutoLogData({ ...autoLogData, fixes: e.target.value })}
                placeholder="Fixed login issue&#10;Resolved tab display bug"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAutoLogModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleQuickLog} 
                disabled={saving || !autoLogData.title || !autoLogData.summary}
                data-testid="submit-quick-log-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Rocket className="h-4 w-4 mr-2" />
                    Create Log
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeploymentLogPage;
